import { useCallback } from 'react';
import Button from '@mui/material/Button';
import PersonTimeline from 'domain/shared/timeline/person';
import { TimelineResponseData } from 'framework/ui/timeline';
import type { AddTimelineItemInputProps } from 'domain/shared/timeline/person/PersonTimelinePageLayout';
import EditNote from '@mui/icons-material/EditNote';
import AddIcon from '@mui/icons-material/AddOutlined';
import { useStaffingProjectContext } from '../context/StaffingProjectContext';
import useStaffingNotesService from 'domain/staffing/dashboard/dashboard/shared/staffingContent/notes/staffingNotesService';
import useStaffingNoteTimelineRegistry, { STAFFING_NOTE_SOURCE } from './useStaffingNoteTimelineRegistry';
import type { FormConfigMap } from 'domain/shared/timeline/person/PersonTimelineLayout';
import type { StaffingNoteItem } from 'domain/shared/staffing';

const STAFFING_PROJECT_TIMELINE_MESSAGE_CONTEXT = 'STAFFING_PROJECT_TIMELINE_MESSAGE_CONTEXT';

const noteToTimelineEntity = (note: StaffingNoteItem) => {
  const dateTime = note.dateTime ?? '';
  return {
    id: note.id,
    timelineId: note.id,
    createdAt: dateTime,
    updatedAt: dateTime,
    createdById: (note as any).createdById ?? 0,
    [STAFFING_NOTE_SOURCE]: note,
  };
};

const notesToTimelineResponse = (notes: StaffingNoteItem[] = []): TimelineResponseData => {
  return {
    result: notes.map(noteToTimelineEntity) as any,
    pageNumber: 0,
    totalPages: 1,
    totalElements: notes.length,
  };
};

const formConfig: FormConfigMap = {
  default: {
    icon: <EditNote />,
    text: 'New note',
  },
};

const AddNoteButton = ({ registries, selectItemCallback }: AddTimelineItemInputProps) => {
  const noteRegistry = registries?.[0];
  if (!noteRegistry) return null;
  return (
    <Button variant="contained" onClick={() => selectItemCallback(noteRegistry)} startIcon={<AddIcon />}>
      Note
    </Button>
  );
};

const StaffingProjectTimelinePage = () => {
  const { project, staffingData, refetch } = useStaffingProjectContext();
  const { createStaffingNote } = useStaffingNotesService();

  const entityId = project?.id?.toString() ?? '';
  const registries = [useStaffingNoteTimelineRegistry({ refetch })];

  const notesResponse = useCallback((): Promise<TimelineResponseData> => {
    const notes = staffingData?.staffingNote ?? [];
    return Promise.resolve(notesToTimelineResponse(notes));
  }, [staffingData?.staffingNote]);

  const getTimelineItems = useCallback((_entityId: string, _request: unknown) => notesResponse(), [notesResponse]);

  const getTimelinePageById = useCallback((_entityId: string, _entryId: string) => notesResponse(), [notesResponse]);

  const createTimelineItem = useCallback(
    async (projectId: string, _itemType: string, request: { noteText: string; attachmentPaths?: string[] }) => {
      const created = await createStaffingNote(projectId, {
        noteText: request.noteText,
        attachmentPaths: request.attachmentPaths ?? [],
      });
      return noteToTimelineEntity(created);
    },
    [createStaffingNote],
  );

  if (!entityId) return null;

  return (
    <PersonTimeline
      entityId={entityId}
      registries={registries}
      getTimelineItems={getTimelineItems}
      createTimelineItem={createTimelineItem}
      getTimelinePageById={getTimelinePageById}
      messageContext={STAFFING_PROJECT_TIMELINE_MESSAGE_CONTEXT}
      formConfig={formConfig}
      AddTimelineItemInput={AddNoteButton}
      noEntriesMessage="No entries in this timeline yet."
    />
  );
};

export default StaffingProjectTimelinePage;
